import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, Input, message, Modal, Tabs } from 'antd';
import { CloseOutlined, DashboardOutlined, LinkOutlined, MoreOutlined } from '@ant-design/icons';
import { HiClock, HiLocationMarker, HiMap } from 'react-icons/hi';
import Swal from 'sweetalert2';
import { UpdateIOT_M_Sensor } from '../../Api/UpdateData';
import { formatRefDate } from '../../utils/formatRefDate';
import SensorDetail from './SensorDetail';
import CurrentField from './CurrentField';
import { vesselStatusLabel } from './monitoringData';

const readingUnit = code => code?.startsWith('TMP') ? '°C' : code?.startsWith('RPM') ? 'RPM'
    : code?.startsWith('LVL') ? 'cm' : code?.startsWith('INC') ? '°' : '';

export default function VesselOperations({ vessel, province, provinceLoading, onClose, onUpdated }) {
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [editedSensors, setEditedSensors] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    useEffect(() => { setDetail(null); setSettingsOpen(false); setActiveTab('overview'); }, [vessel?.key]);
    if (!vessel) return null;
    const sensorActionsAvailable = ['ready', 'saved'].includes(vessel.currentState) && Boolean(vessel.current?.length);

    const openDashboard = () => {
        navigate(`/dashboard?KodeCabang=${vessel.KodeCabang}&ShipCode=${vessel.ShipCode}`);
        localStorage.setItem('Filter', JSON.stringify({
            KodeCabang: vessel.KodeCabang,
            NamaCabang: vessel.NamaCabang,
            ShipCode: vessel.ShipCode,
            ShipName: vessel.ShipName,
        }));
    };
    const openSettings = () => {
        const initialEdited = {};
        vessel.current?.forEach(sensor => {
            initialEdited[sensor.SensorCode] = {
                MaxValue1: sensor.MaxValue1 || 0,
                MaxValue2: sensor.MaxValue2 || 0,
                MaxValue3: sensor.MaxValue3 || 0,
            };
        });
        setEditedSensors(initialEdited);
        setSettingsOpen(true);
    };
    const handleAction = ({ key }) => key === 'Settings' ? openSettings() : setDetail(key);
    const handleMaxValueChange = (sensor, field, value) => setEditedSensors(previous => ({
        ...previous,
        [sensor.SensorCode]: { ...previous[sensor.SensorCode], [field]: value === '' ? 0 : parseFloat(value) || 0 },
    }));
    const saveSettings = async () => {
        if (!sensorActionsAvailable) return;
        try {
            const params = { AccountId: vessel.KodeCabang, ObjectCode: vessel.ShipCode };
            const payload = vessel.current.map(sensor => ({
                AccountId: vessel.KodeCabang,
                ObjectCode: vessel.ShipCode,
                SensorId: sensor.SensorCode,
                MaxValue1: editedSensors[sensor.SensorCode]?.MaxValue1 || sensor.MaxValue1,
                MaxValue2: editedSensors[sensor.SensorCode]?.MaxValue2 || sensor.MaxValue2,
                MaxValue3: editedSensors[sensor.SensorCode]?.MaxValue3 || sensor.MaxValue3,
            }));
            const response = await UpdateIOT_M_Sensor(params, payload);
            if (response.status === 200) {
                await Swal.fire({ icon: 'success', title: 'Success !', text: response.data.message, confirmButtonText: 'OK' });
                setSettingsOpen(false);
                onUpdated();
            }
        } catch {
            message.error('Failed to update !');
        }
    };

    const actions = ['RPM', 'Temperature', 'Level', 'Inclination', 'Settings'].map(key => ({ key, label: key, disabled: !sensorActionsAvailable }));
    const readings = (vessel.current || []).filter(sensor => sensor.SensorCode && !sensor.SensorCode.startsWith('GPS'));
    const tabs = [
        { key: 'overview', label: 'Overview', children: <section className="monitoring-vessel-card__facts" aria-label="Selected vessel overview">
            <div className="monitoring-vessel-card__reading"><HiClock /><div><span>Last sensor reading</span><strong>
                <CurrentField state={vessel.currentState} label={`Loading last reading for ${vessel.ShipCode}`}>{formatRefDate(vessel.lastReading)}</CurrentField>
            </strong></div></div>
            <div className="monitoring-vessel-card__position"><HiLocationMarker /><div><span>Position</span><strong>{vessel.report ? `${vessel.report.lat.toFixed(6)}, ${vessel.report.lng.toFixed(6)}` : '—'}</strong></div></div>
            <div className="monitoring-vessel-card__location"><HiMap /><div><span>Location</span><strong>{provinceLoading ? 'Loading location...' : province || 'Location unavailable'}</strong><small>Last location report <b>{formatRefDate(vessel.report?.reportDate)}</b></small></div></div>
        </section> },
        { key: 'latest', label: 'Latest Data', children: <section className="monitoring-vessel-card__sensor-list" aria-label="Latest sensor data">
            <CurrentField state={vessel.currentState} label={`Loading sensor readings for ${vessel.ShipCode}`}>
            {readings.length ? readings.map(sensor => <div key={sensor.SensorCode} className="monitoring-vessel-card__sensor-row">
                <strong>{sensor.SensorCode}</strong><span>{sensor.DisplayGroupName || 'Sensor'} · {sensor.Value1 == null ? '—' : `${sensor.Value1} ${readingUnit(sensor.SensorCode)}`.trim()}</span>
                <small>{formatRefDate(sensor.RefDate)}</small>
            </div>) : <p className="monitoring-vessel-card__empty">No sensor readings available.</p>}
            </CurrentField>
        </section> },
        { key: 'status', label: 'Sensor Status', children: <section className="monitoring-vessel-card__sensor-list" aria-label="Sensor status">
            <CurrentField state={vessel.currentState} label={`Loading sensor status for ${vessel.ShipCode}`}>
            {readings.length ? readings.map(sensor => <div key={sensor.SensorCode} className="monitoring-vessel-card__sensor-row">
                <strong>{sensor.SensorCode}</strong><Badge status={sensor.Status ? 'success' : 'default'} text={sensor.Status ? 'Active' : 'Inactive'} />
                <small>{formatRefDate(sensor.RefDate)}</small>
            </div>) : <p className="monitoring-vessel-card__empty">No sensor status available.</p>}
            </CurrentField>
        </section> },
    ];
    return <>
        <article className="monitoring-vessel-card" aria-label={`Selected vessel ${vessel.ShipName || vessel.ShipCode}`}>
            <Button className="monitoring-vessel-card__close" type="text" icon={<CloseOutlined />} aria-label="Hide selected vessel details"
                title="Hide selected vessel details" onClick={onClose} />
            <div className="monitoring-panel-heading monitoring-vessel-card__heading">
                <div className="monitoring-panel-title"><span className="monitoring-section-icon" aria-hidden="true"><LinkOutlined /></span><div>
                    <h2>Selected Vessel</h2><p>Updates with map or list selection</p>
                </div></div>
            </div>
            <header className="monitoring-vessel-card__identity">
                <img src="/assets/ship-z.png" alt="" />
                <div className="monitoring-vessel-card__identity-copy">
                    <h2>{vessel.ShipName || vessel.ShipCode || 'Unnamed vessel'}</h2>
                    <span className={`monitoring-vessel-card__status ${vessel.status === true ? 'is-online' : 'is-offline'}`}>
                        <CurrentField state={vessel.currentState} label={`Loading status for ${vessel.ShipCode}`}>{vesselStatusLabel(vessel)}</CurrentField>
                    </span>
                    <dl>
                        <div><dt>Vessel ID</dt><dd>{vessel.ShipCode || '—'}</dd></div>
                        <div><dt>Account</dt><dd>{vessel.KodeCabang || '—'}</dd></div>
                    </dl>
                </div>
            </header>
            <Tabs className="monitoring-vessel-card__tabs" items={tabs} activeKey={activeTab} onChange={setActiveTab} />
            <footer>
                <Button icon={<DashboardOutlined />} onClick={openDashboard}>Open Dashboard</Button>
                <Dropdown trigger={['click']} menu={{ items: actions, onClick: handleAction }}>
                    <Button type="primary" icon={<MoreOutlined />} disabled={!sensorActionsAvailable}>Vessel Actions</Button>
                </Dropdown>
            </footer>
            <p className="monitoring-status-note">Online/Offline follows the existing API sensor status.</p>
        </article>

        <Modal rootClassName="monitoring-detail-modal" title={`${vessel.ShipName || vessel.ShipCode} · ${detail || ''}`}
            open={Boolean(detail)} onCancel={() => setDetail(null)} footer={null} width={760} destroyOnHidden>
            {detail && <CurrentField state={vessel.currentState} label={`Loading sensor details for ${vessel.ShipCode}`}>
                <p className="monitoring-note">Last Reading: {formatRefDate(vessel.lastReading)}</p><SensorDetail record={vessel} kind={detail} />
            </CurrentField>}
        </Modal>

        <Modal rootClassName="monitoring-settings-modal" centered title={<div><h2>SETTING {vessel.ShipName || vessel.ShipCode}</h2><p>Last Update: {formatRefDate(vessel.lastReading)}</p></div>}
            open={settingsOpen} onCancel={() => setSettingsOpen(false)} width={1040} styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
            footer={[<Button key="cancel" onClick={() => setSettingsOpen(false)}>Cancel</Button>, <Button key="save" type="primary" disabled={!sensorActionsAvailable} onClick={saveSettings}>Save Setting</Button>]}>
            <div className="monitoring-settings-grid">
                {vessel.current?.map(sensor => <section key={sensor.SensorCode} className="monitoring-setting-item">
                    <header><strong>{sensor.SensorCode}</strong><span>{sensor.DisplayGroupName}</span></header>
                    <div className="monitoring-setting-fields">
                        {[1, 2, 3].map(number => sensor[`MaxValue${number}`] !== 0 && <label key={number}>Max Value {number}
                            <Input type="number" value={editedSensors[sensor.SensorCode]?.[`MaxValue${number}`] || ''}
                                onChange={event => handleMaxValueChange(sensor, `MaxValue${number}`, event.target.value)} />
                        </label>)}
                    </div>
                    <footer><span>Status</span><Badge status={sensor.Status ? 'success' : 'error'} text={sensor.Status ? 'Active' : 'Inactive'} /></footer>
                </section>)}
            </div>
        </Modal>
    </>;
}

VesselOperations.propTypes = {
    vessel: PropTypes.object,
    province: PropTypes.string,
    provinceLoading: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onUpdated: PropTypes.func.isRequired,
};
